import { Dictionary } from 'lodash';
import { useCallback, useRef } from 'react';
import { useQueryClient } from 'react-query';
import { Team } from 'api/models';
import { Project, ProjectUserLite } from 'api/project';
import { Action, EventType, ObjectType, SocketData } from 'api/pushes';
import {
  FetchableResourceType,
  ResourceType,
  SocketDataForTeamInRm,
  TeamSocketIdentifier,
  UserSocketIdentifier,
} from 'api/resource-management/types';
import { AllocationEventModel } from 'components/organisms/resource-management/scheduler-config/models';
import { GroupBy } from 'components/organisms/resource-management/types';
import { filterAllocationEvents } from 'components/organisms/resource-management/utils/events/events';
import { resourceIdForProject } from 'components/organisms/resource-management/utils/id';
import { RemovedMembers } from 'data/members/slice';
import { getProjectUsersLiteKey } from 'queries/common';
import { ALL_PROJECTS_QUERY_KEY } from 'queries/constants';
import { useUnresolveTeamInLocalState } from 'queries/placeholder';
import { logger } from 'services';
import { useAggregationManager } from '../../aggregation-manager';
import { useReassignAllocationManager } from '../../reassign-allocations-manager';
import {
  useAddRemoveResource,
  useResourceManager,
} from '../../resource-manager';
import { useAllocationAndSummaryManager } from '../../resource-summary-manager';
import { useMultiProjectWsUpdatesInRM } from './multi-project-ws-manager';
import { ProjectMembersWsProps } from './project-members-socket-manager';

export const useProjectMembersDeleteWSManager = (
  props: ProjectMembersWsProps,
): void => {
  const queryClient = useQueryClient();
  const {
    schedulerRef,
    groupBy,
    projectLookup,
    userLookup,
    teamLookup,
    projectChannels,
    rangeLoadedInUI,
  } = props;

  const { removeResource } = useAddRemoveResource(props);
  const { updateAllocationAndSummary } = useAllocationAndSummaryManager(props);
  const { removeAggregationForResource, getAllocatedMinutesFromAggregation } =
    useAggregationManager();
  const { reassignAllocationsFromUserToTeam } =
    useReassignAllocationManager(props);
  const { removeTeamFromUI } = useResourceManager(props);
  const unresolveTeamInLocal = useUnresolveTeamInLocalState();

  const deleteHandler = useRef<(socketData: SocketData<string>) => void>();
  const deleteMemberV1Handler =
    useRef<
      (
        socketData: SocketData<(UserSocketIdentifier | TeamSocketIdentifier)[]>,
      ) => void
    >();

  const removeUserHandler = useCallback(
    (
      projectId: number,
      { user: { userId }, role, referenceTeam }: RemovedMembers,
    ) => {
      const specificProject = projectLookup[projectId];

      if (specificProject == null) {
        return;
      }

      const projectUsers =
        queryClient.getQueryData<Dictionary<ProjectUserLite[]>>(
          getProjectUsersLiteKey(),
        )?.[projectId] ?? [];
      const noOfUserRecordsInProject = projectUsers.reduce(
        (count, { user }) => count + (user.userId === userId ? 1 : 0),
        0,
      );

      if (noOfUserRecordsInProject === 1) {
        const updatedTeamMembers = specificProject.teamMembers?.filter(
          (user) => user.userId !== userId,
        );

        queryClient.setQueryData<Project[]>(
          ALL_PROJECTS_QUERY_KEY,
          (previous) =>
            previous?.map((project) => {
              if (project.projectId === projectId) {
                return {
                  ...project,
                  teamMembers: updatedTeamMembers,
                };
              }

              return project;
            }) ?? [],
        );
      }

      queryClient.setQueryData<Dictionary<ProjectUserLite[]>>(
        getProjectUsersLiteKey(),
        (previous) => ({
          ...previous,
          [specificProject.projectId]: (
            previous?.[specificProject.projectId] ?? []
          ).filter(
            (projectUser) =>
              !(
                projectUser.user.userId === userId &&
                projectUser.role?.roleId === role?.roleId &&
                projectUser.referenceTeam?.teamId === referenceTeam?.teamId
              ),
          ),
        }),
      );
      if (role != null && referenceTeam != null) {
        reassignAllocationsFromUserToTeam({
          projectId,
          userId,
          roleId: role.roleId,
          teamId: referenceTeam.teamId,
        });
        unresolveTeamInLocal(projectId, referenceTeam.teamId);
      } else {
        const allEvents: AllocationEventModel[] =
          schedulerRef.current.instance.eventStore.allRecords;

        const userEvents = filterAllocationEvents(allEvents, {
          resourceIdentifier: {
            type: ResourceType.User,
            userId,
          },
        });
        const projectSpecificEvents = filterAllocationEvents(userEvents, {
          projectId,
        });

        const resourceEvents =
          referenceTeam != null
            ? projectSpecificEvents.filter(
                (event) =>
                  event.get('referenceTeamId') === referenceTeam?.teamId,
              )
            : projectSpecificEvents.filter(
                (event) => event.get('referenceTeamId') == null,
              );

        if (resourceEvents.length > 0) {
          resourceEvents.forEach((event) => {
            event.set('minutes', 0);
            schedulerRef.current.instance.eventStore.remove(event);
          });
        }
      }
      removeResource(projectId, {
        type: ResourceType.User,
        user: userLookup[userId],
        ...(referenceTeam
          ? {
              role,
              referenceTeam,
            }
          : {}),
      });

      updateAllocationAndSummary(
        {
          [FetchableResourceType.UserList]: [userId],
        },
        rangeLoadedInUI,
      ).then(() => {
        removeAggregationForResource(
          {
            type: ResourceType.User,
            userId,
            ...(referenceTeam
              ? {
                  roleId: role?.roleId,
                  referenceTeamId: referenceTeam.teamId,
                }
              : {}),
          },
          projectId,
        );

        if (groupBy === GroupBy.Projects) {
          const totalAllocation = getAllocatedMinutesFromAggregation(projectId);

          const resource = schedulerRef.current.instance.resourceStore.getById(
            resourceIdForProject(projectId),
          );

          if (resource) {
            resource.set(
              'totalAllocation',
              totalAllocation.totalAllocatedMinutes,
            );
          }
        }
      });
    },
    [
      getAllocatedMinutesFromAggregation,
      groupBy,
      projectLookup,
      queryClient,
      rangeLoadedInUI,
      reassignAllocationsFromUserToTeam,
      removeAggregationForResource,
      removeResource,
      schedulerRef,
      unresolveTeamInLocal,
      updateAllocationAndSummary,
      userLookup,
    ],
  );
  const removeTeamHandler = useCallback(
    (projectId: number, team: Team) => {
      removeTeamFromUI(projectId, team);
    },
    [removeTeamFromUI],
  );

  const handleMembersDelete = useCallback(
    (socketData: SocketData<string>): void => {
      logger.log('project-members-delete ~ socketData', socketData);
      try {
        const { data, projectId } = socketData;
        const members: (RemovedMembers | SocketDataForTeamInRm)[] =
          JSON.parse(data);

        members.forEach((item) => {
          if ((item as RemovedMembers).user != null) {
            removeUserHandler(projectId, item as RemovedMembers);
          } else if ((item as SocketDataForTeamInRm).team != null) {
            removeTeamHandler(
              projectId,
              teamLookup[(item as SocketDataForTeamInRm).team.teamId],
            );
          }
        });
      } catch (error) {
        logger.error('Error while deleting project members', { error });
      }
    },
    [removeTeamHandler, removeUserHandler, teamLookup],
  );

  deleteHandler.current = handleMembersDelete;

  const deleteHandlerProxy = useCallback((params) => {
    deleteHandler.current?.(params);
  }, []);
  const handleMemberV1Delete = useCallback(
    (
      socketData: SocketData<(UserSocketIdentifier | TeamSocketIdentifier)[]>,
    ) => {
      const { data, projectId } = socketData;
      const projectUsers =
        queryClient.getQueryData<Dictionary<ProjectUserLite[]>>(
          getProjectUsersLiteKey(),
        )?.[projectId] ?? [];

      data.forEach((item) => {
        if (item.identityType === 'USER') {
          const otherVersionsOfUserInProject = projectUsers.filter(
            ({ user: { userId }, role, referenceTeam }) =>
              userId === item.userId && (role != null || referenceTeam != null),
          );

          otherVersionsOfUserInProject.forEach(
            ({ user, role, referenceTeam }) => {
              if (role != null && referenceTeam != null) {
                reassignAllocationsFromUserToTeam({
                  projectId,
                  userId: user.userId,
                  roleId: role.roleId,
                  teamId: referenceTeam.teamId,
                });
                unresolveTeamInLocal(projectId, referenceTeam.teamId);
              }

              removeUserHandler(projectId, { user, role, referenceTeam });
            },
          );

          removeUserHandler(projectId, { user: { userId: item.userId } });
        }
      });
    },
    [
      queryClient,
      reassignAllocationsFromUserToTeam,
      removeUserHandler,
      unresolveTeamInLocal,
    ],
  );

  deleteMemberV1Handler.current = handleMemberV1Delete;

  const deleteMemberV1HandlerProxy = useCallback((params) => {
    deleteMemberV1Handler.current?.(params);
  }, []);

  useMultiProjectWsUpdatesInRM(
    projectChannels,
    deleteHandlerProxy,
    EventType(Action.Delete, ObjectType.ProjectMembersV2),
  );
  useMultiProjectWsUpdatesInRM(
    projectChannels,
    deleteMemberV1HandlerProxy,
    EventType(Action.Delete, ObjectType.ProjectMembers),
  );
};
